// pages/Companies.tsx - GÜNCELLENMİŞ MODAL SİSTEMİ
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  InputAdornment,
  Divider,
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Visibility as ViewIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { Company } from '../types';
import { companiesAPI, ticketsAPI, usersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface CompanyWithStats extends Company {
  totalTickets: number;
  activeTickets: number;
  resolvedTickets: number;
  customer_count?: number;
  ticket_count?: number;
}

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  is_active: boolean;
}

// 🎯 YENİ: Özel Modal Interface
interface CustomModal {
  open: boolean;
  type: 'success' | 'error' | 'info' | 'warning' | 'delete' | 'delete_success';
  title: string;
  message: string;
  companyName?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
}

const isValidEmail = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// 🎯 MASKELİ ŞİFRE FONKSİYONU
const getMaskedPassword = (password: string): string => {
  if (!password) return '••••••••';
  if (password === 'password') return '••••••••';
  
  // Eğer zaten masked ise aynısını döndür
  if (password.startsWith('•') || password === '••••••••') {
    return password;
  }
  
  // Gerçek şifreyi maskele
  const length = password.length;
  if (length <= 4) return '••••';
  if (length > 20) return '•'.repeat(20);
  
  return '•'.repeat(length);
};

const Companies: React.FC = () => {
  const { user: authUser, hasPermission } = useAuth();
  const [companies, setCompanies] = useState<CompanyWithStats[]>([]);
  const [tickets, setTickets] = useState<any[]>([]);
  const [companyUsers, setCompanyUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openUsersDialog, setOpenUsersDialog] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<CompanyWithStats | null>(null);
  
  // 🎯 YENİ: Özel Modal State
  const [customModal, setCustomModal] = useState<CustomModal>({
    open: false,
    type: 'info',
    title: '',
    message: '',
    companyName: '',
  });

  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' as 'success' | 'error' | 'info' | 'warning'
  });
  const [showPassword, setShowPassword] = useState(false);

  // 🎯 LOCALSTORAGE İLE KALICI HAFIZA
  const [passwordHistory, setPasswordHistory] = useState<{[key: number]: string}>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('companyPasswords');
      return saved ? JSON.parse(saved) : {};
    }
    return {};
  });

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    username: '',
    password: '',
  });

  // 🎯 YENİ: Özel Modal Açma Fonksiyonları
  const showCustomModal = (modalConfig: Omit<CustomModal, 'open'>) => {
    setCustomModal({ ...modalConfig, open: true });
  };

  const closeCustomModal = () => {
    setCustomModal(prev => ({ ...prev, open: false }));
  };

  const showSuccessModal = (title: string, message: string, companyName?: string) => {
    showCustomModal({
      type: 'success',
      title,
      message,
      companyName,
    });
  };

  const showErrorModal = (title: string, message: string, companyName?: string) => {
    showCustomModal({
      type: 'error',
      title,
      message,
      companyName,
    });
  };

const showDeleteModal = (company: CompanyWithStats) => {
  setCustomModal({
    open: true,
    type: 'delete',
    title: 'Firma Silme Onayı',
    message: `"${company.name}" firmasını silmek istediğinizden emin misiniz?\n\nBu işlem geri alınamaz. Firma, kullanıcılar ve talepler silinecektir.`,
    companyName: company.name,
    onConfirm: () => handleDeleteConfirm(company),
    onCancel: () => setCustomModal(prev => ({ ...prev, open: false }))
  });
};

  const showDeleteSuccessModal = (companyName: string) => {
    showCustomModal({
      type: 'delete_success',
      title: 'Firma Silindi',
      message: `Firma başarıyla silindi.\n\nTüm firma verileri ve ilişkili kayıtlar sistemden kaldırıldı.`,
      companyName: companyName,
    });
  };

  // 🎯 YENİ: Modal İkonları ve Renkleri
  const getModalConfig = (type: CustomModal['type']) => {
    const config = {
      success: {
        icon: <SuccessIcon sx={{ fontSize: 64, color: 'success.main' }} />,
        color: 'success.main' as const,
        bgColor: 'success.50' as const,
      },
      error: {
        icon: <ErrorIcon sx={{ fontSize: 64, color: 'error.main' }} />,
        color: 'error.main' as const,
        bgColor: 'error.50' as const,
      },
      info: {
        icon: <InfoIcon sx={{ fontSize: 64, color: 'info.main' }} />,
        color: 'info.main' as const,
        bgColor: 'info.50' as const,
      },
      warning: {
        icon: <WarningIcon sx={{ fontSize: 64, color: 'warning.main' }} />,
        color: 'warning.main' as const,
        bgColor: 'warning.50' as const,
      },
      delete: {
        icon: <ErrorIcon sx={{ fontSize: 64, color: 'error.main' }} />,
        color: 'error.main' as const,
        bgColor: 'error.50' as const,
      },
      delete_success: {
        icon: <SuccessIcon sx={{ fontSize: 64, color: 'success.main' }} />,
        color: 'success.main' as const,
        bgColor: 'success.50' as const,
      },
    };
    return config[type];
  };

  // 🎯 PASSWORD HISTORY DEĞİŞİNCE LOCALSTORAGE'A KAYDET
  useEffect(() => {
    localStorage.setItem('companyPasswords', JSON.stringify(passwordHistory));
  }, [passwordHistory]);

  useEffect(() => {
    loadData();
  }, []);

// Companies.tsx - loadData fonksiyonunu güncelle
const loadData = async () => {
  try {
    setLoading(true);
    const [companiesRes, ticketsRes] = await Promise.all([
      companiesAPI.getCompanies(),
      ticketsAPI.getTickets()
    ]);
    
    setTickets(ticketsRes.data);
    
    // İstatistikleri hesapla
    const companiesWithStats: CompanyWithStats[] = companiesRes.data.map((company: Company) => {
      const companyTickets = ticketsRes.data.filter((ticket: any) => 
        ticket.company_id === company.id
      );
      const activeTickets = companyTickets.filter((ticket: any) => 
        [1, 2, 3, 4, 5, 7].includes(ticket.status_id)
      ).length;
      const resolvedTickets = companyTickets.filter((ticket: any) => 
        [6, 8].includes(ticket.status_id)
      ).length;
      
      return {
        ...company,
        totalTickets: companyTickets.length,
        activeTickets,
        resolvedTickets,
      };
    });
    
    // 🎯 AKSAṂ TEKNOLOJİ'Yİ EN ÜSTE KİLİTLE - ID 1
    const sortedCompanies = companiesWithStats.sort((a: CompanyWithStats, b: CompanyWithStats) => {
      // Aksan Teknoloji (ID: 1) her zaman en üstte
      if (a.id === 1 && b.id !== 1) return -1;
      if (a.id !== 1 && b.id === 1) return 1;
      
      // Diğer firmaları alfabetik sırala
      return a.name.localeCompare(b.name, 'tr');
    });
    
    setCompanies(sortedCompanies);
  } catch (error) {
    console.error('❌ Load data error:', error);
    showErrorModal('Veri Yükleme Hatası', 'Firma verileri yüklenirken bir hata oluştu.');
  } finally {
    setLoading(false);
  }
};
  const loadCompanyUsers = async (companyId: number) => {
    try {
      setUsersLoading(true);
      const usersRes = await usersAPI.getCompanyUsers(companyId);
      setCompanyUsers(usersRes.data || []);
    } catch (error) {
      console.error('❌ Load company users error:', error);
      setCompanyUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleAddCompany = () => {
    setSelectedCompany(null);
    setShowPassword(true); 
    setFormData({
      name: '',
      email: '',
      phone: '',
      username: '',
      password: 'password',
    });
    setOpenDialog(true);
  };

  const handleEditCompany = async (company: CompanyWithStats) => {
    setSelectedCompany(company);
    setShowPassword(false);
    
    // 🎯 LOCALSTORAGE'DAN ŞİFREYİ AL
    const savedPassword = passwordHistory[company.id] || 'password';
    
    setFormData({
      name: company.name || '',
      email: company.email || '',
      phone: company.phone || '',
      username: '',
      password: savedPassword,
    });
    
    setOpenDialog(true);
  };

  const handleViewCompanyUsers = async (company: CompanyWithStats) => {
    setSelectedCompany(company);
    await loadCompanyUsers(company.id);
    setOpenUsersDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedCompany(null);
    setShowPassword(false);
    setFormData({
      name: '',
      email: '',
      phone: '',
      username: '',
      password: 'password',
    });
  };

  const handleCloseUsersDialog = () => {
    setOpenUsersDialog(false);
    setSelectedCompany(null);
    setCompanyUsers([]);
  };

  const handleFormChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

// Companies.tsx - handleSaveCompany içine debug ekleyin
const handleSaveCompany = async () => {
  try {
    if (!formData.name.trim() || !formData.email.trim()) {
      showErrorModal('Eksik Bilgi', 'Firma adı ve email zorunludur.');
      return;
    }

    if (!isValidEmail(formData.email)) {
      showErrorModal('Geçersiz Email', 'Lütfen geçerli bir email adresi giriniz.');
      return;
    }

    const companyData: any = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim() || '',
    };

    console.log('🎯 DEBUG - Form Verileri:', {
      showPassword,
      formDataPassword: formData.password,
      maskedPassword: getMaskedPassword(formData.password),
      isMasked: formData.password === getMaskedPassword(formData.password),
      selectedCompanyId: selectedCompany?.id,
      selectedCompanyName: selectedCompany?.name
    });

    // 🎯 ŞİFRE KONTROLÜ - ÇOK NET
    const isPasswordChanged = showPassword && 
                            formData.password && 
                            formData.password !== 'password' && 
                            formData.password !== getMaskedPassword(formData.password);

    console.log('🎯 DEBUG - Şifre Değişiklik Durumu:', {
      isPasswordChanged,
      conditions: {
        showPassword,
        hasPassword: !!formData.password,
        notDefault: formData.password !== 'password',
        notMasked: formData.password !== getMaskedPassword(formData.password)
      }
    });

    if (selectedCompany) {
      // Şifre değiştiyse ve masked değilse companyData'ya ekle
      if (selectedCompany.id !== 1 && isPasswordChanged) {
        companyData.password = formData.password;
        console.log('🔑 DEBUG - Şifre backend\'e gönderilecek:', formData.password);
      } else {
        console.log('🔑 DEBUG - Şifre backend\'e GÖNDERİLMEYECEK');
        // Şifre göndermiyoruz
      }

      // FIRMA GÜNCELLEME
      console.log('📤 DEBUG - Backend\'e gönderilen data:', companyData);
      const response = await companiesAPI.updateCompany(selectedCompany.id, companyData);
      console.log('✅ DEBUG - Backend response:', response.data);
      
      // Şifre güncellendiyse localStorage'a kaydet
      if (selectedCompany.id !== 1 && isPasswordChanged) {
        setPasswordHistory(prev => ({
          ...prev,
          [selectedCompany!.id]: formData.password
        }));
        
        showSuccessModal(
          'Firma ve Şifre Güncellendi', 
          `Firma bilgileri ve şifre başarıyla güncellendi.\n\nYeni şifre: ${formData.password}`,
          selectedCompany.name
        );
      } else {
        showSuccessModal(
          'Firma Güncellendi', 
          `Firma bilgileri başarıyla güncellendi.`,
          selectedCompany.name
        );
      }
      
    } else {
      // YENİ FIRMA OLUŞTURMA
      companyData.password = formData.password.trim() || 'password';
      console.log('📤 DEBUG - Yeni firma şifresi:', companyData.password);
      
      const response = await companiesAPI.createCompany(companyData);
      
      if (response.data && response.data.company) {
        const newCompanyId = response.data.company.id;
        setPasswordHistory(prev => ({
          ...prev,
          [newCompanyId]: companyData.password
        }));
      }
      
      showSuccessModal(
        'Firma Oluşturuldu', 
        `Yeni firma başarıyla oluşturuldu.\n\nFirma: ${formData.name}\nEmail: ${formData.email}\nŞifre: ${companyData.password}`,
        formData.name
      );
    }
    
    handleCloseDialog();
    loadData();
    
  } catch (error: any) {
    console.error('❌ Save company error:', error);
    console.error('❌ Error details:', error.response?.data);
    showErrorModal(
      'İşlem Hatası', 
      error.response?.data?.error || 'İşlem sırasında beklenmeyen bir hata oluştu.'
    );
  }
};
  // 🎯 YENİ: Silme Onayı Fonksiyonu
const handleDeleteConfirm = async (company: CompanyWithStats) => {
  try {
    await companiesAPI.deleteCompany(company.id);
    
    // Başarı mesajı
    setCustomModal({
      open: true,
      type: 'success',
      title: 'Firma Silindi',
      message: `"${company.name}" firması ve ilişkili tüm kullanıcılar başarıyla silindi.`,
      companyName: company.name,
    });
    
    // LocalStorage'dan şifreyi temizle
    setPasswordHistory(prev => {
      const newHistory = { ...prev };
      delete newHistory[company.id];
      return newHistory;
    });
    
    // Listeyi yenile
    loadData();
    
  } catch (error: any) {
    console.error('❌ Delete company error:', error);
    setCustomModal({
      open: true,
      type: 'error',
      title: 'Silme Hatası',
      message: error.response?.data?.error || 'Silme işlemi sırasında bir hata oluştu.',
      companyName: company.name,
    });
  }
};
  const handleDeleteCompany = async (company: CompanyWithStats) => {
    showDeleteModal(company);
  };

  // Toplam istatistikler
  const totalStats = {
    companies: companies.length,
    totalTickets: tickets.length,
    activeTickets: tickets.filter((t: any) => [1, 2, 3, 4, 5, 7].includes(t.status_id)).length,
  };

  // 🎯 DEBUG: localStorage içeriğini göster
  const debugLocalStorage = () => {
    console.log('🔍 localStorage companyPasswords:', localStorage.getItem('companyPasswords'));
    console.log('🔍 passwordHistory state:', passwordHistory);
    showCustomModal({
      type: 'info',
      title: 'Debug Bilgisi',
      message: 'localStorage ve passwordHistory bilgileri konsolda görüntülendi.',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Firmalar yükleniyor...</Typography>
      </Box>
    );
  }

  if (!hasPermission('admin')) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h5" color="error">
          ⚠️ Bu sayfaya erişim izniniz yok
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 2 }}>
          Sadece admin kullanıcılar firma yönetim sayfasına erişebilir.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* DEBUG BUTONU - Geliştirme sırasında kullanabilirsiniz */}
      <Button 
        variant="outlined" 
        size="small" 
        onClick={debugLocalStorage}
        sx={{ position: 'absolute', top: 10, right: 10 }}
      >
        🔍 Debug localStorage
      </Button>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          🏢 Firmalar ({companies.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddCompany}
        >
          Yeni Firma
        </Button>
      </Box>

      {/* İstatistik Kartları */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Paper sx={{ p: 2, flex: 1, textAlign: 'center' }}>
          <Typography variant="h6" color="primary.main">
            {totalStats.companies}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Toplam Firma
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1, textAlign: 'center' }}>
          <Typography variant="h6" color="primary.main">
            {totalStats.totalTickets}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Toplam Talep
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1, textAlign: 'center' }}>
          <Typography variant="h6" color="warning.main">
            {totalStats.activeTickets}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Aktif Talep
          </Typography>
        </Paper>
      </Box>

      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Firma Adı</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>İletişim</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>Toplam Talep</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>Aktif Talep</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>Çözülen Talep</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>İşlemler</TableCell>
              </TableRow>
            </TableHead>
<TableBody>
  {companies.map((company) => (
    <TableRow 
      key={company.id} 
      hover
      sx={{ 
        backgroundColor: company.id === 1 ? 'primary.50' : 'inherit',
        borderLeft: company.id === 1 ? '4px solid' : 'none',
        borderLeftColor: company.id === 1 ? 'primary.main' : 'transparent',
        '&:hover': {
          backgroundColor: company.id === 1 ? 'primary.100' : 'action.hover',
        }
      }}
    >
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography 
            variant="subtitle2" 
            fontWeight="600" 
            sx={{ 
              fontSize: '1rem',
              color: company.id === 1 ? 'primary.main' : 'inherit'
            }}
          >
            {company.name}
          </Typography>
          {company.id === 1 && (
            <Chip 
              label="Ana Firma" 
              size="small" 
              color="primary"
              variant="filled"
              sx={{ 
                fontWeight: 'bold',
                fontSize: '0.75rem'
              }}
            />
          )}
        </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        📧 {company.email || 'Belirtilmemiş'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        📞 {company.phone || 'Belirtilmemiş'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <Chip 
                      label={company.totalTickets}
                      color="primary" 
                      variant="filled"
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <Chip 
                      label={company.activeTickets}
                      color={company.activeTickets > 0 ? "warning" : "default"}
                      variant="filled"
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <Chip 
                      label={company.resolvedTickets}
                      color="success" 
                      variant="filled"
                      size="small"
                    />
                  </TableCell>
<TableCell sx={{ textAlign: 'center' }}>
  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
    <IconButton 
      color="info"
      onClick={() => handleViewCompanyUsers(company)}
      size="small"
      title="Kullanıcıları Görüntüle"
    >
      <ViewIcon />
    </IconButton>
    <IconButton 
      color="primary" 
      onClick={() => handleEditCompany(company)}
      size="small"
      title="Firma Düzenle"
    >
      <EditIcon />
    </IconButton>
    
    {/* ⚠️ AKSAṂ TEKNOLOJİ SİLME BUTONU İNAKTİF - ID 1 */}
  <IconButton 
    color={company.id === 1 ? "default" : "error"}
    onClick={company.id === 1 ? undefined : () => handleDeleteCompany(company)}
    size="small"
    disabled={company.id === 1}
    sx={{
      color: company.id === 1 ? 'grey.600' : 'error.main',
      '&:hover': {
        backgroundColor: company.id === 1 ? 'grey.100' : 'error.light',
      },
      cursor: company.id === 1 ? 'not-allowed' : 'pointer'
    }}
  >
    <DeleteIcon />
  </IconButton>
  </Box>
</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* 🎯 YENİ: ÖZEL MODAL SİSTEMİ */}
      <Dialog 
        open={customModal.open} 
        onClose={customModal.type === 'delete' ? customModal.onCancel : closeCustomModal}
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
          }
        }}
      >
        <DialogContent sx={{ p: 4, textAlign: 'center' }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            gap: 3 
          }}>
            {/* İkon */}
            {getModalConfig(customModal.type).icon}
            
            {/* Başlık */}
            <Typography 
              variant="h4" 
              fontWeight="bold"
              sx={{ 
                color: getModalConfig(customModal.type).color,
                fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                letterSpacing: '0.5px'
              }}
            >
              {customModal.title}
            </Typography>
            
            {/* Firma Adı (Varsa) */}
            {customModal.companyName && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography 
                  variant="h5" 
                  fontWeight="600"
                  sx={{ 
                    color: 'primary.main',
                    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                    background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                    backgroundClip: 'text',
                    textFillColor: 'transparent',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 1
                  }}
                >
                  {customModal.companyName}
                </Typography>
                <Divider sx={{ my: 1 }} />
              </Box>
            )}
            
{/* Mesaj */}
<Typography 
  variant="body1" 
  color="text.secondary"
  sx={{ 
    whiteSpace: 'pre-line',
    lineHeight: 1.8,
    fontSize: '1.1rem',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
  }}
>
  {customModal.message.split('\n').map((line, index) => {
    if (line.includes('Firma:') || line.includes('Email:') || line.includes('Şifre:')) {
      const parts = line.split(':');
      return (
        <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1 }}>
          <Typography component="span" sx={{ fontWeight: 'bold', color: 'black', minWidth: 60 }}>
            {parts[0]}:
          </Typography>
          <Typography component="span" sx={{ flex: 1 }}>
            {parts.slice(1).join(':')}
          </Typography>
        </Box>
      );
    }
    return (
      <Typography key={index} component="div" sx={{ mb: 1 }}>
        {line}
      </Typography>
    );
  })}
</Typography>
          </Box>
        </DialogContent>
        
<DialogActions sx={{ 
  justifyContent: 'center', 
  pb: 4,
  gap: 2 
}}>
  {customModal.type === 'delete' ? (
    <>
      <Button 
        variant="outlined" 
        onClick={customModal.onCancel}
        size="large"
        sx={{ 
          minWidth: 120,
          borderColor: 'grey.400',
          color: 'grey.700',
          '&:hover': {
            borderColor: 'grey.600',
            backgroundColor: 'grey.50'
          }
        }}
      >
        İptal
      </Button>
      <Button 
        variant="contained" 
        color="error"
        onClick={customModal.onConfirm}
        size="large"
        startIcon={<DeleteIcon />}
        sx={{ 
          minWidth: 120,
          background: 'linear-gradient(45deg, #D32F2F 30%, #D32F2F 90%)',
          boxShadow: '0 3px 5px 2px rgba(255, 82, 82, .3)',
          '&:hover': {
            background: 'linear-gradient(45deg, #D32F2F 30%, #D32F2F 90%)',
          }
        }}
      >
        Sil
      </Button>
    </>
  ) : (
    <Button 
      variant="contained" 
      onClick={closeCustomModal}
      size="large"
      sx={{ 
        minWidth: 120,
        background: customModal.type === 'delete_success' || customModal.type === 'success'
          ? 'linear-gradient(45deg, #4CAF50 30%, #66BB6A 90%)'
          : `linear-gradient(45deg, ${getModalConfig(customModal.type).color} 30%, ${getModalConfig(customModal.type).color}99 90%)`,
        boxShadow: customModal.type === 'delete_success' || customModal.type === 'success'
          ? '0 3px 5px 2px rgba(76, 175, 80, .3)'
          : `0 3px 5px 2px rgba(33, 150, 243, .3)`,
        '&:hover': {
          background: customModal.type === 'delete_success' || customModal.type === 'success'
            ? 'linear-gradient(45deg, #43A047 30%, #4CAF50 90%)'
            : `linear-gradient(45deg, ${getModalConfig(customModal.type).color} 30%, ${getModalConfig(customModal.type).color}CC 90%)`,
        }
      }}
    >
      Tamam
    </Button>
  )}
</DialogActions>
      </Dialog>

      {/* Firma Ekleme/Düzenleme Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedCompany ? '✏️ Firma Düzenle' : '🏢 Yeni Firma Ekle'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Firma Adı *"
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email *"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                  size="small"
                  placeholder="firma@ornek.com"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Telefon"
                  value={formData.phone}
                  onChange={(e) => handleFormChange('phone', e.target.value)}
                  size="small"
                  placeholder="05xx xxx xx xx"
                />
              </Grid>
              
{/* Şifre Alanı */}
{selectedCompany?.id !== 1 && (
  <Grid item xs={12} sm={6}>
    <TextField
      fullWidth
      label="Şifre"
      type={showPassword ? "text" : "password"}
      value={showPassword ? formData.password : getMaskedPassword(formData.password)}
      onChange={(e) => {
        // Her zaman değişikliği kabul et
        handleFormChange('password', e.target.value);
      }}
      onFocus={() => {
        // Eğer masked şifre görünüyorsa, gerçek şifreyi sıfırla
        if (!showPassword && formData.password === getMaskedPassword(formData.password)) {
          handleFormChange('password', 'password');
        }
      }}
      size="small"
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              onClick={() => setShowPassword(!showPassword)}
              edge="end"
              size="small"
              title={showPassword ? "Şifreyi gizle" : "Şifreyi göster"}
            >
              {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </IconButton>
          </InputAdornment>
        ),
      }}
      helperText={
        showPassword 
          ? `Gerçek şifre görünüyor - değiştirebilirsiniz`
          : `Şifre gizli - değiştirmek için göz ikonuna tıklayın veya alana tıklayın`
      }
    />
  </Grid>
)}
            </Grid>
            
            {/* AKSAN TEKNOLOJİ ÖZEL MESAJI */}
            {selectedCompany?.id === 1 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Aksan Teknoloji Özel Durumu</strong><br/>
                  • Bu firma için birden fazla kullanıcı bulunmaktadır<br/>
                  • Şifre yönetimi kullanıcı bazında yapılır<br/>
                  • Sadece firma iletişim bilgilerini güncelleyebilirsiniz
                </Typography>
              </Alert>
            )}

            {/* Yeni Firma Bilgilendirmesi */}
            {!selectedCompany && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>⚠️ ÖNEMLİ: Bu firma için bir kullanıcı oluşturulacak</strong><br/>
                  • <strong>Email:</strong> {formData.email || "[email]"}<br/>
                  • <strong>Şifre:</strong> {formData.password}<br/>
                  • <strong>Rol:</strong> Müşteri<br/>
                  <br/>
                </Typography>
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>İptal</Button>
          <Button variant="contained" onClick={handleSaveCompany}>
            {selectedCompany ? 'Güncelle' : 'Oluştur'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Kullanıcıları Görüntüleme Dialog */}
      <Dialog open={openUsersDialog} onClose={handleCloseUsersDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon color="primary" />
            <Typography variant="h6">
              {selectedCompany?.name} - Kullanıcı Bilgileri
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {usersLoading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Kullanıcılar yükleniyor...
                </Typography>
              </Box>
            ) : companyUsers.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <PersonIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Kullanıcı Bulunamadı
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Bu firma için henüz kullanıcı oluşturulmamış.
                </Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {companyUsers.map((user) => (
                    <Card 
                      key={user.id} 
                      variant="outlined"
                      sx={{
                        border: '1px solid',
                        borderColor: user.role === 'Admin' ? 'primary.main' : 
                                    user.role === 'Destek' ? 'secondary.main' : 'success.main',
                        backgroundColor: user.role === 'Admin' ? 'primary.50' : 
                                        user.role === 'Destek' ? 'secondary.50' : 'success.50'
                      }}
                    >
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {user.name}
                              </Typography>
                              <Chip 
                                label={user.role} 
                                size="small" 
                                color={
                                  user.role === 'Admin' ? 'primary' : 
                                  user.role === 'Destek' ? 'secondary' : 'success'
                                }
                                variant="filled"
                              />
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <EmailIcon fontSize="small" color="action" />
                                <Typography variant="body2" color="text.secondary">
                                  {user.email}
                                </Typography>
                              </Box>
                              
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Chip 
                                  label={user.is_active ? '🟢 Aktif' : '🔴 Pasif'} 
                                  size="small" 
                                  color={user.is_active ? 'success' : 'default'}
                                  variant="outlined"
                                />
                                <Typography variant="caption" color="text.secondary">
                                  User ID: {user.id}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>

                          {selectedCompany?.id !== 1 && user.role === 'Müşteri' && (
                            <Box sx={{ textAlign: 'center', minWidth: 80 }}>
                              <Typography variant="caption" display="block" color="text.secondary">
                                Firma Yetkilisi
                              </Typography>
                              <Chip 
                                label="Ana Kullanıcı" 
                                size="small" 
                                color="primary"
                                variant="filled"
                              />
                            </Box>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>

                {selectedCompany?.id === 1 && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>Aksan Teknoloji</strong> ana firma olduğu için birden fazla kullanıcı bulunmaktadır.
                      Admin ve destek personelleri burada listelenmektedir.
                    </Typography>
                  </Alert>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUsersDialog}>Kapat</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Companies;